/**
 * 维修工单列表页
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Space, Input, Select, Button, Card, DatePicker, Row, Col, Modal, Form, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { repairApi } from '../../services/api';

const { RangePicker } = DatePicker;

const statusMap = {
  pending: '待接修', received: '已接修', quoting: '待付款', paid: '已付款',
  repairing: '维修中', to_ship: '待发货', shipped: '已发货', completed: '已完成', cancelled: '已取消',
};
const statusColors = {
  pending: 'default', received: 'blue', quoting: 'orange', paid: 'cyan',
  repairing: 'processing', to_ship: 'gold', shipped: 'green', completed: 'success', cancelled: 'error',
};

function RepairList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: '', keyword: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const navigate = useNavigate();

  const columns = [
    { title: '维修单号', dataIndex: 'repair_no', key: 'repair_no', width: 170 },
    { title: '客户', dataIndex: 'customer_name', key: 'customer_name', width: 100 },
    { title: '电话', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: '故障描述', dataIndex: 'fault_desc', key: 'fault_desc', ellipsis: true, width: 200 },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s) => <Tag color={statusColors[s]}>{statusMap[s] || s}</Tag>,
    },
    {
      title: '费用', dataIndex: 'total_fee', key: 'total_fee', width: 100,
      render: (v) => v > 0 ? `¥${parseFloat(v).toFixed(2)}` : '-',
    },
    { title: '维修人员', dataIndex: 'technician_name', key: 'technician_name', width: 100, render: (v) => v || '-' },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作', key: 'action', width: 80, fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/repair/detail/${record.id}`)}>详情</Button>
      ),
    },
  ];

  const loadData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await repairApi.list({ page, pageSize, ...filters });
      setData(res.data?.list || []);
      setPagination({
        page: res.data?.pagination?.page || 1,
        pageSize: res.data?.pagination?.pageSize || 20,
        total: res.data?.pagination?.total || 0,
      });
    } catch (err) {
      console.error('加载列表失败', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleSearch = () => loadData(1, pagination.pageSize);
  const handleReset = () => { setFilters({ status: '', keyword: '' }); setTimeout(() => loadData(), 0); };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      await repairApi.create(values);
      message.success('维修工单创建成功');
      setCreateOpen(false);
      createForm.resetFields();
      loadData();
    } catch (err) {
      // 表单校验失败或接口报错，已在拦截器处理
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col><Input placeholder="搜索单号/客户/电话" prefix={<SearchOutlined />} value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })} onPressEnter={handleSearch}
            style={{ width: 250 }} allowClear /></Col>
          <Col>
            <Select placeholder="工单状态" value={filters.status || undefined} allowClear style={{ width: 140 }}
              onChange={v => setFilters({ ...filters, status: v || '' })}>
              {Object.entries(statusMap).map(([k, v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}
            </Select>
          </Col>
          <Col><Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button></Col>
          <Col><Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button></Col>
          <Col flex="auto" />
          <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>新建工单</Button></Col>
        </Row>
      </Card>

      <Table rowKey="id" columns={columns} dataSource={data} loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (page, pageSize) => loadData(page, pageSize),
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="新建维修工单"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        confirmLoading={createLoading}
        okText="创建"
        cancelText="取消"
        width={600}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户姓名" rules={[{ required: true, message: '请输入客户姓名' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="return_address" label="回寄地址" rules={[{ required: true, message: '请输入回寄地址' }]}>
            <Input placeholder="客户寄回物品的地址" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact_person" label="联系人" rules={[{ required: true, message: '请输入联系人' }]}>
                <Input placeholder="请输入" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sn_number" label="SN/机身号">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="fault_desc" label="故障描述" rules={[{ required: true, message: '请描述故障现象' }]}>
            <Input.TextArea rows={3} placeholder="请描述设备故障现象" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_express_company" label="快递公司（如有）">
                <Input placeholder="如：顺丰" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_express_no" label="快递单号（如有）">
                <Input placeholder="客户已寄出快递单号" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}

export default RepairList;
